/**
 * Spreadsheet import.
 *
 * A coach's existing squad list is whatever they already keep: a CSV exported
 * from a federation site, an .xlsx with a header row three lines down, a block
 * pasted from Google Sheets. The job here is to get their file in without
 * asking them to reformat it first.
 */

import { parseBirthDate } from '@shared/birthdate';
import { matchPosition, type PositionOption } from '@shared/positions';

// Date reading lives in shared/birthdate.ts: the form, the importer and the
// Worker's validation must agree on what '03/2008' means, and three copies of
// the rule would agree only until one of them was edited.
export { parseBirthDate };

export type Row = string[];

export const IMPORT_FIELDS = [
  'fullName',
  'firstName',
  'lastName',
  'knownAs',
  'shirtNumber',
  'dateOfBirth',
  'nationality',
  'strongFoot',
  'heightCm',
  'weightKg',
  'primaryPositionKey',
  'secondaryPositionKey',
  'statusKey',
] as const;

export type ImportField = (typeof IMPORT_FIELDS)[number];

/** Header names seen in real squad lists, in the three working languages. */
const HEADER_ALIASES: Record<ImportField, string[]> = {
  /**
   * Plenty of squad lists keep one name column. Matched before firstName and
   * lastName so a sheet with a single "Name" heading lands here rather than
   * having half the name silently dropped.
   */
  fullName: [
    'full name', 'fullname', 'name', 'player', 'player name', 'full',
    'nom complet', 'nom et prenom', 'nom & prenom', 'prenom et nom', 'joueur', 'nom prenom',
    'الاسم الكامل', 'الاسم والنسب', 'اللاعب', 'الاسم و النسب',
  ],
  firstName: ['first name', 'firstname', 'first', 'given name', 'prénom', 'prenom', 'الاسم', 'الإسم'],
  lastName: ['last name', 'lastname', 'surname', 'family name', 'nom', 'nom de famille', 'النسب', 'اللقب', 'العائلة'],
  knownAs: ['known as', 'nickname', 'usual name', "nom d'usage", 'surnom', 'اسم الشهرة'],
  shirtNumber: ['number', 'no', 'no.', 'n°', 'num', 'numéro', 'numero', 'dossard', 'shirt', 'shirt number', '#', 'الرقم'],
  dateOfBirth: ['date of birth', 'dob', 'birth date', 'birthdate', 'born', 'date de naissance', 'naissance', 'né le', 'ne le', 'تاريخ الميلاد', 'الميلاد',
    'year of birth', 'birth year', 'année de naissance', 'annee de naissance', 'sn', 'سنة الميلاد'],
  nationality: ['nationality', 'nationalité', 'nationalite', 'country', 'pays', 'الجنسية'],
  strongFoot: ['foot', 'strong foot', 'preferred foot', 'pied', 'pied fort', 'القدم', 'القدم القوية'],
  heightCm: ['height', 'height (cm)', 'taille', 'taille (cm)', 'الطول'],
  weightKg: ['weight', 'weight (kg)', 'poids', 'poids (kg)', 'الوزن'],
  primaryPositionKey: ['position', 'pos', 'pos.', 'main position', 'poste', 'poste principal', 'المركز'],
  secondaryPositionKey: ['second position', 'secondary position', 'poste secondaire', 'المركز الثاني'],
  statusKey: ['status', 'squad status', 'statut', 'الحالة'],
};

const normalise = (s: string): string =>
  s
    .trim()
    .toLowerCase()
    .normalize('NFD')
    // Strip accents so "Prénom" matches "prenom" without listing both.
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ');

/**
 * Picks the delimiter by counting candidates outside quotes on the first line.
 * Semicolons are the default CSV separator in French and Arabic Excel locales,
 * which is exactly the file this product will be handed.
 */
export function detectDelimiter(text: string): string {
  const firstLine = text.split(/\r?\n/, 1)[0] ?? '';
  const counts = [',', ';', '\t', '|'].map((d) => ({
    d,
    n: firstLine.split(d).length - 1,
  }));
  counts.sort((a, b) => b.n - a.n);
  return counts[0] && counts[0].n > 0 ? counts[0].d : ',';
}

/**
 * RFC-4180-style parsing: quoted fields may contain the delimiter, newlines and
 * escaped quotes. A naive split on commas mangles "Ait Taleb, Marwan" and every
 * address-like field, which is the classic import bug.
 */
export function parseDelimited(text: string, delimiter?: string): Row[] {
  const d = delimiter ?? detectDelimiter(text);
  const rows: Row[] = [];
  let row: Row = [];
  let field = '';
  let inQuotes = false;
  let i = 0;

  // A BOM from Excel would otherwise become part of the first header name.
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;

  while (i < src.length) {
    const ch = src[i]!;

    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += ch;
      i += 1;
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (ch === d) {
      row.push(field);
      field = '';
      i += 1;
      continue;
    }
    if (ch === '\n' || ch === '\r') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      i += src[i] === '\r' && src[i + 1] === '\n' ? 2 : 1;
      continue;
    }
    field += ch;
    i += 1;
  }

  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  // Trailing blank lines are normal in exported files.
  return rows.filter((r) => r.some((cell) => cell.trim() !== ''));
}

/**
 * Finds the header row. Exports often carry a club name or an export date above
 * the real header, so the first line is not reliably it — take the first line
 * that matches at least two known field names.
 */
export function findHeaderRow(rows: Row[]): number {
  for (let i = 0; i < Math.min(rows.length, 10); i++) {
    const guessed = guessMapping(rows[i] ?? []);
    if (Object.keys(guessed).length >= 2) return i;
  }
  return 0;
}

export function guessMapping(headers: Row): Partial<Record<ImportField, number>> {
  const mapping: Partial<Record<ImportField, number>> = {};
  const seen = new Set<number>();

  for (const field of IMPORT_FIELDS) {
    const aliases = HEADER_ALIASES[field].map(normalise);
    for (let col = 0; col < headers.length; col++) {
      if (seen.has(col)) continue;
      const header = normalise(headers[col] ?? '');
      if (header && aliases.includes(header)) {
        mapping[field] = col;
        seen.add(col);
        break;
      }
    }
  }
  return mapping;
}

const FOOT_VALUES: Record<string, 'left' | 'right' | 'both'> = {
  l: 'left', left: 'left', gauche: 'left', g: 'left', يسار: 'left', يسرى: 'left',
  r: 'right', right: 'right', droit: 'right', droite: 'right', d: 'right', يمين: 'right', يمنى: 'right',
  b: 'both', both: 'both', two: 'both', deux: 'both', 'les deux': 'both', كلتا: 'both',
};

export function parseFoot(raw: string): 'left' | 'right' | 'both' | undefined {
  return FOOT_VALUES[normalise(raw)];
}


export function parseNumber(raw: string): number | undefined {
  // Comma decimals are normal in French and Arabic locales.
  const value = raw.trim().replace(',', '.');
  if (!value) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

/** Particles that begin a family name rather than ending a given one. */
const SURNAME_PARTICLES = new Set([
  // Moroccan and Arabic
  'ait', 'aït', 'el', 'al', 'ben', 'bin', 'bou', 'abou', 'abu', 'abd', 'ould', 'ouled', 'oulad',
  // European, for imported or dual-national squads
  'van', 'von', 'de', 'del', 'della', 'di', 'da', 'dos', 'das', 'la', 'le', 'du', 'des', 'mac', 'mc', "o'",
]);

export interface SplitName {
  firstName: string;
  lastName: string;
}

/**
 * Splits one name column into given and family name.
 *
 * Three conventions appear in real squad lists, and guessing wrong puts the
 * wrong word on the team sheet:
 *
 *  - "Benali, Youssef" — a comma always separates family name from given name
 *  - "BENALI Youssef" / "Youssef BENALI" — the FIFA convention, family name in
 *    capitals, which can lead or trail
 *  - "Youssef Benali" — plain, family name last
 *
 * The last case is where a naive split fails in this context: "Marwan Ait Taleb"
 * and "Anass El Fassi" have two-word family names. A leading particle marks
 * where the family name starts, so those survive intact while a compound given
 * name like "Mohamed Amine Bennani" still splits correctly.
 */
export function splitFullName(value: string): SplitName | null {
  const raw = value.trim().replace(/\s+/g, ' ');
  if (!raw) return null;

  // A comma is unambiguous: family name first.
  if (raw.includes(',')) {
    const [family = '', given = ''] = raw.split(',', 2).map((x) => x.trim());
    if (family && given) return { firstName: given, lastName: family };
    if (family) return { firstName: '', lastName: family };
  }

  const parts = raw.split(' ').filter(Boolean);
  if (parts.length === 1) return { firstName: parts[0]!, lastName: '' };

  // Family name in capitals, wherever it sits. Requires a mixed-case token to
  // compare against, so an entirely upper-case row is not misread.
  const isCaps = (w: string) => w.length > 1 && w === w.toLocaleUpperCase() && w !== w.toLocaleLowerCase();
  const capsCount = parts.filter(isCaps).length;
  if (capsCount > 0 && capsCount < parts.length) {
    const family = parts.filter(isCaps).join(' ');
    const given = parts.filter((w) => !isCaps(w)).join(' ');
    return { firstName: given, lastName: family };
  }

  // Otherwise the family name starts at the first particle, or at the last word.
  const particleAt = parts.findIndex(
    (w, i) => i > 0 && SURNAME_PARTICLES.has(w.toLocaleLowerCase()),
  );
  const cut = particleAt > 0 ? particleAt : parts.length - 1;
  return {
    firstName: parts.slice(0, cut).join(' '),
    lastName: parts.slice(cut).join(' '),
  };
}

export interface MappedPlayer {
  row: number;
  values: Record<string, unknown>;
  issues: string[];
}

/** Turns mapped cells into the payload shape, reporting what could not be read. */
export function mapRow(
  row: Row,
  mapping: Partial<Record<ImportField, number>>,
  rowNumber: number,
  /** The club's own positions. Without them, nothing is assumed. */
  positions: readonly PositionOption[] = [],
): MappedPlayer {
  const cell = (field: ImportField): string => {
    const col = mapping[field];
    return col === undefined ? '' : (row[col] ?? '').trim();
  };

  const values: Record<string, unknown> = {};
  const issues: string[] = [];

  const first = cell('firstName');
  const last = cell('lastName');
  const full = cell('fullName');

  // Explicit columns win when both are present. Otherwise the whole name may be
  // sitting in one column — whether that column is headed "Full name", "Name"
  // or "Prénom" — so it is split rather than stored whole.
  if (first && last) {
    values.firstName = first;
    values.lastName = last;
  } else if (full) {
    const split = splitFullName(full);
    values.firstName = first || split?.firstName || '';
    values.lastName = last || split?.lastName || '';
  } else {
    const source = first || last || '';
    const split = splitFullName(source);
    if (split?.lastName) {
      // Two or more words: the column held a whole name.
      values.firstName = split.firstName;
      values.lastName = split.lastName;
    } else if (split) {
      // A single word means whatever the column was headed. One word under
      // "Nom" is a family name, not a given one.
      if (last) values.lastName = source;
      else values.firstName = source;
    }
  }

  if (!values.firstName) {
    // One name and nothing else is still a player: keep it rather than refusing
    // the row, and say which half is missing.
    if (values.lastName) values.firstName = String(values.lastName);
    issues.push('missing_first_name');
  }

  if (!values.lastName) issues.push('missing_last_name');

  const knownAs = cell('knownAs');
  if (knownAs) values.knownAs = knownAs;

  const nationality = cell('nationality');
  if (nationality) values.nationality = nationality;

  const shirt = cell('shirtNumber');
  if (shirt) {
    const n = parseNumber(shirt);
    if (n !== undefined && Number.isInteger(n) && n >= 1 && n <= 99) values.shirtNumber = n;
    else issues.push('bad_shirt_number');
  }

  const dob = cell('dateOfBirth');
  if (dob) {
    // Partial dates are kept as they came: a list that gives only a birth year
    // is still a usable list, and inventing a day would read as a fact.
    const parsed = parseBirthDate(dob);
    if (parsed) values.dateOfBirth = parsed;
    else issues.push('bad_date_of_birth');
  }

  const foot = cell('strongFoot');
  if (foot) {
    const parsed = parseFoot(foot);
    if (parsed) values.strongFoot = parsed;
    else issues.push('bad_foot');
  }

  for (const [field, key, min, max] of [
    ['heightCm', 'heightCm', 100, 230],
    ['weightKg', 'weightKg', 25, 160],
  ] as const) {
    const raw = cell(field);
    if (!raw) continue;
    const n = parseNumber(raw);
    if (n !== undefined && n >= min && n <= max) values[key] = n;
    else issues.push(`bad_${key}`);
  }

  // Positions are resolved against the club's own configuration, because a
  // squad list says "Gardien" or "DC" far more often than it says "GK". An
  // unrecognised value is reported rather than stored: a position the app
  // cannot draw or group looks, on screen, exactly like no position at all.
  const pos = cell('primaryPositionKey');
  if (pos) {
    const matched = matchPosition(pos, positions);
    if (matched) values.primaryPositionKey = matched.key;
    else issues.push('unknown_position');
  }
  const pos2 = cell('secondaryPositionKey');
  if (pos2) {
    const matched = matchPosition(pos2, positions);
    if (matched) values.secondaryPositionKey = matched.key;
    else issues.push('unknown_position');
  }

  const status = cell('statusKey');
  if (status) values.statusKey = status.toLowerCase().replace(/\s+/g, '_');

  return { row: rowNumber, values, issues };
}

/**
 * Columns the coach did not map to one of our fields.
 *
 * A squad list carries things this product has no field for — a licence number,
 * a parent's phone, a school, a bus route — and dropping them silently means the
 * coach keeps the spreadsheet as well as the product. These become the club's
 * own fields, named after the heading they came from.
 */
export function unmappedColumns(
  headers: readonly string[],
  mapping: Partial<Record<ImportField, number>>,
): number[] {
  const used = new Set(Object.values(mapping).filter((i): i is number => i !== undefined));
  return headers.map((_, i) => i).filter((i) => !used.has(i));
}

export interface CustomColumn {
  /** Index into the sheet's columns. */
  index: number;
  /** What the field will be called. Editable: a heading is often an abbreviation. */
  label: string;
  keep: boolean;
}

/** Proposes a custom field per unmapped column that has anything in it. */
export function proposeCustomColumns(
  headers: readonly string[],
  rows: readonly Row[],
  mapping: Partial<Record<ImportField, number>>,
): CustomColumn[] {
  return unmappedColumns(headers, mapping).map((index) => {
    const heading = (headers[index] ?? '').trim();
    // A column with a heading but no data is a spreadsheet artefact; one with
    // data but no heading is worth keeping, under a name the coach can fix.
    const hasData = rows.some((r) => (r[index] ?? '').trim() !== '');
    return {
      index,
      label: heading || `Column ${index + 1}`,
      keep: hasData,
    };
  });
}

/** The custom values for one row, keyed by field label. */
export function customValues(
  row: Row,
  columns: readonly CustomColumn[],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const column of columns) {
    if (!column.keep) continue;
    const value = (row[column.index] ?? '').trim();
    if (value) out[column.label] = value;
  }
  return out;
}
